import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material';
import { ArrowRight2, Filter } from 'iconsax-react';
import { type FC, useState } from 'react';

import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import {
  EMPTY_NOTIFICATION_FILTER_VALUES,
  type NotificationFilterValues,
} from '@core/utils/notificationQueryParams';
import type { NotificationTab } from '@core/utils/notifications';

import { ButtonWithPopover } from '../ButtonWithPopover/ButtonWithPopover';
import { SlabCard } from '../SlabCard/SlabCard';

enum FilterSection {
  RecordType = 'recordType',
  Date = 'date',
}

const SECTION_LABELS: Record<FilterSection, string> = {
  [FilterSection.RecordType]: 'Record Type',
  [FilterSection.Date]: 'Date',
};

// Reminders have no creation date to filter on, so the Date section is left out there.
const TAB_FILTER_SECTIONS: Record<NotificationTab, FilterSection[]> = {
  unread: [FilterSection.RecordType, FilterSection.Date],
  inbox: [FilterSection.RecordType, FilterSection.Date],
  scheduled: [FilterSection.RecordType],
};

const RECORD_TYPE_OPTIONS = [
  { recordType: Enums.ReminderUserSettingsRecordType.Quote, label: 'Quotes' },
  { recordType: Enums.ReminderUserSettingsRecordType.Project, label: 'Projects' },
  { recordType: Enums.ReminderUserSettingsRecordType.Activity, label: 'Activities' },
];

const DATE_OPTIONS = [
  { days: null, label: 'Any time' },
  { days: 1, label: 'Today' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
];

type Props = {
  tab: NotificationTab;
  value: NotificationFilterValues;
  onChange: (value: NotificationFilterValues) => void;
};

export const NotificationCenterFilters: FC<Props> = ({ tab, value, onChange }) => {
  const sections = TAB_FILTER_SECTIONS[tab];
  const [selectedSection, setSelectedSection] = useState(FilterSection.RecordType);
  const activeSection = sections.includes(selectedSection) ? selectedSection : sections[0];
  const { recordTypes, lastDays } = value;
  const isDateApplied = sections.includes(FilterSection.Date) && hasValue(lastDays);

  const toggleRecordType = (option: Enums.ReminderUserSettingsRecordType): void => {
    onChange({
      ...value,
      recordTypes: recordTypes.includes(option)
        ? recordTypes.filter((item) => item !== option)
        : [...recordTypes, option],
    });
  };

  const appliedFiltersCount = [recordTypes.length > 0, isDateApplied].filter(Boolean).length;
  const hasAppliedFilters = appliedFiltersCount > 0;

  return (
    <ButtonWithPopover
      sx={{ padding: '8px 10px' }}
      variant='slab-action-bordered'
      paperSx={{
        boxShadow: '0 5px 10px rgba(0,0,0,0.10)',
        marginY: 2,
      }}
      startIcon={
        <Badge badgeContent={appliedFiltersCount} color='primary'>
          <Filter variant='TwoTone' />
        </Badge>
      }
    >
      <SlabCard padding={1} sx={{ width: '28rem', marginY: 0 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' paddingBottom={1}>
          <Typography variant='h6'>Applied filters:</Typography>
          <Button
            size='small'
            variant='text'
            disabled={!hasAppliedFilters}
            onClick={() => onChange(EMPTY_NOTIFICATION_FILTER_VALUES)}
            sx={{
              fontSize: '18px',
              fontWeight: 400,
            }}
          >
            Reset
          </Button>
        </Box>
        <Divider sx={{ borderColor: 'SlabGray.Stroke', marginY: 0, opacity: 1 }} />
        <Box display='flex'>
          <Box display='flex' flexDirection='column' flex='0 0 40%' paddingY={1.5}>
            {sections.map((section) => {
              const isActive = section === activeSection;

              return (
                <Box
                  key={section}
                  onClick={() => setSelectedSection(section)}
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  padding={1.5}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isActive ? 'SlabIndigo.50' : 'transparent',
                  }}
                >
                  <Typography variant='body1'>{SECTION_LABELS[section]}</Typography>
                  <ArrowRight2 size={16} />
                </Box>
              );
            })}
          </Box>
          <Divider
            sx={{ borderColor: 'SlabGray.Stroke', margin: 0, opacity: 1 }}
            orientation='vertical'
            flexItem
          />
          <Box display='flex' flexDirection='column' flex={1} paddingY={1.5} gap={1} paddingX={1.5}>
            {activeSection === FilterSection.RecordType &&
              RECORD_TYPE_OPTIONS.map(({ recordType, label }) => (
                <FormControlLabel
                  key={recordType}
                  label={label}
                  control={
                    <Checkbox
                      checked={recordTypes.includes(recordType)}
                      onChange={() => toggleRecordType(recordType)}
                    />
                  }
                />
              ))}
            {activeSection === FilterSection.Date &&
              DATE_OPTIONS.map(({ days, label }) => (
                <FormControlLabel
                  key={label}
                  label={label}
                  control={
                    <Radio
                      checked={lastDays === days}
                      onChange={() => onChange({ ...value, lastDays: days })}
                    />
                  }
                />
              ))}
          </Box>
        </Box>
      </SlabCard>
    </ButtonWithPopover>
  );
};
